import axios from 'axios';

// Clean up corrupted tokens from storage
try {
  const t = localStorage.getItem('token');
  if (!t || t === 'null' || t === 'undefined' || t.length < 10) {
    localStorage.removeItem('token');
  }
  const st = sessionStorage.getItem('token');
  if (!st || st === 'null' || st === 'undefined' || st.length < 10) {
    sessionStorage.removeItem('token');
  }
} catch(e) {}

// Create axios instance
const getBaseUrl = () => {
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== 'http://localhost:5000/api') {
    return process.env.REACT_APP_API_URL;
  }
  return 'https://drvivara.cloud/api';
};

const api = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000, // Increased to 30s for mobile networks
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor to add auth token and guard against undefined URLs
api.interceptors.request.use(
  (config) => {
    if (!config.url || typeof config.url !== 'string' || config.url.includes('undefined')) {
      console.error('⚠️ Blocked request to undefined URL:', config.url);
      return Promise.reject(new Error('Invalid URL'));
    }
    let token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined' || token.length < 10) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      token = null;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and silent refresh
api.interceptors.response.use(
  (response) => {
    // Only save token from auth endpoints to avoid accidental overwrites
    const isAuthEndpoint = response.config?.url?.startsWith('/auth/');
    if (response.data?.token && isAuthEndpoint) {
      localStorage.setItem('token', response.data.token);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn('⚠️ 401 Unauthorized detected - attempting silent refresh');
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        const res = await axios.post(`${getBaseUrl()}/auth/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${currentToken}`
          }
        });

        if (res.data?.success && res.data?.token) {
          localStorage.setItem('token', res.data.token);
          originalRequest.headers.Authorization = `Bearer ${res.data.token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Silent refresh failed');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        window.dispatchEvent(new Event('auth_logout'));
        // If NOT on an auth page, redirect to patient login
        const hash = window.location.hash;
        const path = hash.replace('#', '') || window.location.pathname;
        const isAuthPage = path.startsWith('/auth/') || path === '/admin/login' ||
                           path === '/admin' || path === '/role-selection' ||
                           path === '/forgot-password' || path === '/reset-password';
        if (!isAuthPage) {
          window.location.href = '#/auth/patient/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authAPI = {
  login: (role, data) => api.post(`/auth/login/${role}`, data),
  register: (role, data) => api.post(`/auth/register/${role}`, data),
  googleLogin: (data) => api.post('/auth/google', data),
  googleExchange: (data) => api.post('/auth/google/exchange', data),
  appleLogin: (data) => api.post('/auth/apple', data),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  uploadDocuments: (data) => api.post('/auth/upload-documents', data),
  uploadDocumentsInternal: (formData) => api.post('/auth/upload-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  saveDeviceToken: (token) => api.post('/auth/device-token', { token }),
  saveVoipToken: (token) => api.post('/auth/voip-token', { token }),
  uploadProfileImage: (formData) => api.post('/auth/profile-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadTempImage: (formData) => api.post('/auth/upload-temp-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  changePassword: (data) => api.put('/auth/change-password', data),
  deleteAccount: (data) => api.delete('/auth/delete-account', { data }), // Note: Axios delete body goes in 'data' config
  verifyAppleReceipt: (data) => api.post('/apple-iap/verify-receipt', data),
  restoreApplePurchases: (data) => api.post('/apple-iap/restore-purchases', data),
};

// Admin API endpoints
export const adminAPI = {
  login: (data) => api.post('/admin/login', data),
  getDashboard: () => api.get('/admin/dashboard'),
  getAllUsers: (filters) => api.get('/admin/users/all', { params: filters }),
  getPendingUsers: () => api.get('/admin/users/pending'),
  approveUser: (userId) => api.put(`/admin/approve/user/${userId}`),
  rejectUser: (userId) => api.put(`/admin/reject/user/${userId}`),
  getPendingMedicines: () => api.get('/admin/medicines/pending'),
  approveMedicine: (medicineId) => api.put(`/admin/approve/medicine/${medicineId}`),
  setConsultationFee: (data) => api.put('/admin/set-consultation-fee', data),
  setDeductionPercent: (data) => api.put('/admin/set-deduction-percent', data),
  getSubscriptionPlans: () => api.get('/admin/subscriptions/plans'),
  createSubscriptionPlan: (data) => api.post('/admin/subscriptions/plans', data),
  updateSubscriptionPlan: (planId, data) => api.put(`/admin/subscriptions/plans/${planId}`, data),
  deleteSubscriptionPlan: (planId) => api.delete(`/admin/subscriptions/plans/${planId}`),
  // Content Management - Sliders
  getAllSliders: () => api.get('/admin/content/sliders'),
  createSlider: (data, onProgress, signal) => api.post('/admin/content/sliders', data, {
    onUploadProgress: onProgress,
    signal
  }),
  updateSlider: (id, data, onProgress, signal) => api.put(`/admin/content/sliders/${id}`, data, {
    onUploadProgress: onProgress,
    signal
  }),
  deleteSlider: (id) => api.delete(`/admin/content/sliders/${id}`),
  // Content Management - Yoga Videos
  getAllYogaVideos: () => api.get('/admin/content/yoga'),
  createYogaVideo: (data) => api.post('/admin/content/yoga', data),
  updateYogaVideo: (id, data) => api.put(`/admin/content/yoga/${id}`, data),
  deleteYogaVideo: (id) => api.delete(`/admin/content/yoga/${id}`),
  // Content Management - Food Links
  getAllFoodLinks: () => api.get('/admin/content/food-links'),
  createFoodLink: (data) => api.post('/admin/content/food-links', data),
  updateFoodLink: (id, data) => api.put(`/admin/content/food-links/${id}`, data),
  deleteFoodLink: (id) => api.delete(`/admin/content/food-links/${id}`),
  // Withdrawal Management
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  getWithdrawalById: (id) => api.get(`/admin/withdrawals/${id}`),
  approveWithdrawal: (id, notes) => api.put(`/admin/withdrawals/${id}/approve`, { admin_notes: notes }),
  rejectWithdrawal: (id, notes) => api.put(`/admin/withdrawals/${id}/reject`, { admin_notes: notes }),
  addWithdrawalNotes: (id, data) => api.post(`/admin/withdrawals/${id}/notes`, data),
  assignDoctorToOrg: (data) => api.put('/admin/users/assign-doctor', data),
  // Content Management - Podcasts
  getAllPodcasts: () => api.get('/admin/content/podcasts'),
  createPodcast: (data) => api.post('/admin/content/podcasts', data),
  updatePodcast: (id, data) => api.put(`/admin/content/podcasts/${id}`, data),
  deletePodcast: (id) => api.delete(`/admin/content/podcasts/${id}`),
  getDashboardStats: () => api.get('/admin/dashboard'),
  approveMedicineByAdmin: (id) => api.put(`/admin/approve/medicine/${id}`, { is_approved: true }),
  getSupportTickets: (status) => api.get(`/support/tickets${status ? `?status=${status}` : ''}`),
  updateTicket: (id, data) => api.put(`/support/tickets/${id}`, data),
  getBlueTickPrice: () => api.get('/admin/blue-tick/price'),
  setBlueTickPrice: (price) => api.post('/admin/blue-tick/price', { price }),
  getPendingBlueTickUsers: () => api.get('/admin/blue-tick/pending'),
  approveBlueTick: (userId) => api.put(`/admin/blue-tick/approve/${userId}`),
  sendCustomNotification: (data) => api.post('/admin/notifications/send', data),
  // User Management
  searchUsers: (params) => api.get('/admin/users/search', { params }),
  getUserById: (userId) => api.get(`/admin/users/${userId}`),
  editUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  suspendUser: (userId) => api.put(`/admin/users/${userId}/suspend`),
  activateUser: (userId) => api.put(`/admin/users/${userId}/activate`),
  resetUserPassword: (userId, data) => api.put(`/admin/users/${userId}/reset-password`, data),
  getUserSubscriptions: (userId) => api.get(`/admin/users/${userId}/subscriptions`),
  changeUserSubscription: (userId, data) => api.put(`/admin/users/${userId}/subscription`, data),
  // Feature Flags
  getFeatureFlags: () => api.get('/feature-flags'),
  updateFeatureFlag: (feature, data) => api.put(`/feature-flags/${feature}`, data),
  // Consultation Pricing
  getConsultationPricing: () => api.get('/consultation-pricing'),
  updateConsultationPricing: (data) => api.put('/consultation-pricing', data),
  // Reels
  getReels: (params) => api.get('/reels', { params }),
  getMyReels: () => api.get('/reels/my'),
  getCreatorReels: (creatorId) => api.get(`/reels/creator/${creatorId}`),
  getReelById: (id) => api.get(`/reels/${id}`),
  createReel: (data) => api.post('/reels', data),
  interactReel: (reelId, data) => api.post(`/reels/${reelId}/interact`, data),
  getReelComments: (reelId) => api.get(`/reels/${reelId}/comments`),
  // Admin Reels
  adminGetReels: (params) => api.get('/admin/reels', { params }),
  adminApproveReel: (reelId) => api.put(`/admin/reels/${reelId}/approve`),
  adminRejectReel: (reelId, data) => api.put(`/admin/reels/${reelId}/reject`, data),
  adminDeleteReel: (reelId) => api.delete(`/admin/reels/${reelId}`),
  adminFeatureReel: (reelId) => api.put(`/admin/reels/${reelId}/feature`),
  adminBanCreator: (userId) => api.put(`/admin/reels/ban-creator/${userId}`),
  adminReelsAnalytics: () => api.get('/admin/reels/analytics'),
  getInteractionVolume: (days = 30) => api.get(`/admin/analytics/interactions?days=${days}`),
  getGrowthStats: (period = 'month') => api.get(`/admin/analytics/growth?period=${period}`),
  // Reel Subscriptions
  getMyReelSubscription: () => api.get('/reel-subscriptions/my'),
  createReelSubscription: (data) => api.post('/reel-subscriptions/create', data)
};


// Consultation API endpoints
export const consultationAPI = {
  request: (data) => api.post('/consultation/request', data),
  checkLimits: (patientId) => api.get(`/consultation/check-limits/${patientId}`),
  getHistory: (userId) => api.get(`/consultation/history/${userId}`),
  start: (consultationId) => api.put(`/consultation/start/${consultationId}`),
  complete: (consultationId) => api.put(`/consultation/complete/${consultationId}`),
  sendMessage: (consultationId, data) => api.post(`/consultation/${consultationId}/messages`, data),
  getMessages: (consultationId) => api.get(`/consultation/${consultationId}/messages`),
  // Prescriptions
  createPrescription: (consultationId, data) => api.post(`/consultation/${consultationId}/prescription`, data),
  getPrescription: (consultationId) => api.get(`/consultation/${consultationId}/prescription`),
  // Health Profile
  updateHealthProfile: (data) => api.put('/consultation/health-profile', data),
  reject: (consultationId, data) => api.post(`/consultation/${consultationId}/reject`, data),
  // Payment confirmation
  confirmPayment: (data) => api.post('/consultation/confirm-payment', data)
};

export const prescriptionAPI = {
  create: (data) => api.post('/prescription/create', data),
  complete: (data) => api.post('/prescription/complete', data),
  send: (id) => api.post(`/prescription/send/${id}`),
  getDoctorPrescriptions: (params) => api.get('/prescription/doctor/all', { params }),
  getPatientPrescriptions: (params) => api.get('/prescription/patient/all', { params }),
  // Get full prescription by consultation ID (for patient view)
  getPrescriptionByConsultation: (consultationId) => api.get(`/prescription/consultation/${consultationId}`),
  delete: (id) => api.delete(`/prescription/${id}`),
  download: (id) => `${process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api'}/prescription/download/${id}`
};

// Payment API endpoints
export const paymentAPI = {
  createRazorpayOrder: (data) => api.post('/payments/razorpay/create-order', data),
  verifySubscription: (data) => api.post('/payments/razorpay/verify-subscription', data),
  handleWebhook: (data) => api.post('/payments/razorpay/webhook', data),
  getWalletBalance: (userId) => api.get(`/payments/wallet/balance/${userId}`),
  requestWithdrawal: (data) => api.post('/payments/wallet/withdraw', data)
};

// Subscription API (for patients - plans visible after login)
export const subscriptionAPI = {
  getPlans: (role) => api.get('/subscriptions/plans', { params: { role } })
};

// Content API endpoints (public)
export const contentAPI = {
  getSliders: () => api.get('/content/sliders'),
  getYogaSessions: () => api.get('/content/yoga'),
  getPodcasts: () => api.get('/content/podcasts'),
  getFoodLinks: () => api.get('/content/food-links')
};

// Doctor API endpoints
export const doctorAPI = {
  getApprovedDoctors: () => api.get('/doctors/approved'),
  searchDoctors: (params) => api.get('/doctors/search', { params }), // Helper for new endpoint
  getHospitalDoctors: () => api.get('/doctors/hospital'),
  getClinicDoctors: () => api.get('/doctors/clinic'),
  getDoctorById: (doctorId) => api.get(`/doctors/${doctorId}`),
  getStats: () => api.get('/doctor/stats'),
  getAppointments: () => api.get('/doctor/appointments'),
  acceptConsultation: (id) => api.post(`/consultation/${id}/accept`),
  getWallet: () => api.get('/payments/wallet/balance/doctor'),
  requestBlueTick: (paymentId) => api.post('/doctor/request-blue-tick', { paymentId }),
  getReviews: (doctorId, params) => api.get(`/doctors/${doctorId}/reviews`, { params }),
  getBlueTickPrice: () => api.get('/doctor/blue-tick/price')
};

// Review API
export const reviewAPI = {
  add: (data) => api.post('/reviews/add', data)
};

// Retailer API endpoints
export const retailerAPI = {
  getStats: () => api.get('/retailer/stats'),
  getProducts: () => api.get('/retailer/products'),
  updateStock: (id, stock) => api.put(`/retailer/product/${id}/stock`, { stock_quantity: stock }),
  getOrders: () => api.get('/retailer/orders'),
  getWallet: () => api.get('/payments/wallet/balance/retailer'),
  getProfile: () => api.get('/retailer/profile')
};

// Clinic API endpoints
export const clinicAPI = {
  getStats: () => api.get('/clinic/stats'),
  getAppointments: () => api.get('/clinic/appointments'),
  getProfile: () => api.get('/clinic/profile'),
  getDoctors: () => api.get('/clinic/doctors'),
  addDoctor: (data) => api.post('/clinic/add-doctor', data),
  removeDoctor: (doctorId) => api.delete(`/clinic/doctors/${doctorId}`)
};

// Hospital API endpoints
export const hospitalAPI = {
  getStats: () => api.get('/hospital/stats'),
  getDoctors: () => api.get('/hospital/doctors'),
  addDoctor: (data) => api.post('/hospital/add-doctor', data),
  getWallet: () => api.get('/payments/wallet/balance/hospital'),
  getProfile: () => api.get('/hospital/profile')
};

// Patient Specific Features
export const patientAPI = {
  getChatHistory: () => api.get('/patient/chat-history'),
  getCallHistory: () => api.get('/patient/call-history'),
  submitSupportTicket: (data) => api.post('/support/ticket', data)
};

// Medicine API endpoints
export const medicineAPI = {
  getApprovedMedicines: () => api.get('/medicines/approved'),
  getMedicineById: (id) => api.get(`/medicines/${id}`)
};

// Settings API
export const settingsAPI = {
  getCommission: () => api.get('/admin/settings/commission'),
  setCommission: (data) => api.post('/admin/settings/commission', data)
};

// Order API
export const orderAPI = {
  create: (data) => api.post('/orders/create', data),
  verify: (data) => api.post('/orders/verify', data),
  getMyOrders: () => api.get('/orders/my-orders'),
  getPatientOrders: () => api.get('/orders/my-orders'),
  getRetailerOrders: () => api.get('/orders/retailer/list'),
  updateStatus: (orderId, status) => api.put(`/orders/${orderId}/status`, { status })
};

// Notification API
export const notificationAPI = {
  getNotifications: () => api.get('/notifications'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  deleteNotification: (id) => api.delete(`/notifications/${id}`)
};

// Appointment API
export const appointmentAPI = {
  bookAppointment: (data) => api.post('/appointments/book', data),
  getMyAppointments: (params) => api.get('/appointments/my', { params }),
  getUpcoming: () => api.get('/appointments/upcoming'),
  getHistory: () => api.get('/appointments/history'),
  reschedule: (id, data) => api.put(`/appointments/${id}/reschedule`, data),
  cancel: (id) => api.put(`/appointments/${id}/cancel`),
  markNoShow: (id) => api.put(`/appointments/${id}/no-show`),
  getAvailableSlots: (doctorId, date) => api.get(`/appointments/available-slots/${doctorId}/${date}`),
  getDoctorAvailability: (doctorId) => api.get(`/appointments/availability/${doctorId}`),
  updateAvailability: (data) => api.put('/appointments/availability', data),
};

// Medical Record API
export const medicalRecordAPI = {
  uploadRecord: (formData) => api.post('/medical-records/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPatientRecords: (patientId) => api.get(`/medical-records/patient/${patientId}`),
  getDoctorAuthorized: (patientId) => api.get(`/medical-records/authorized/${patientId}`),
  getTimeline: (patientId) => api.get(`/medical-records/timeline/${patientId}`),
  getFile: (recordId) => api.get(`/medical-records/file/${recordId}`, { responseType: 'blob' }),
  deleteRecord: (recordId) => api.delete(`/medical-records/${recordId}`),
  getAdminRecords: (params) => api.get('/medical-records/admin/all', { params }),
};

// Inventory API
export const inventoryAPI = {
  getInventory: (params) => api.get('/inventory', { params }),
  addItem: (data) => api.post('/inventory', data),
  updateItem: (id, data) => api.put(`/inventory/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/${id}`),
  updateStock: (id, qty) => api.put(`/inventory/${id}/stock`, { quantity: qty }),
  getExpiring: () => api.get('/inventory/expiring'),
  validatePrescription: (data) => api.post('/inventory/validate-prescription', data),
};

// Emergency API
export const emergencyAPI = {
  requestEmergency: (data) => api.post('/emergency/request', data),
  getMyRequests: () => api.get('/emergency/my'),
  getAllRequests: (params) => api.get('/emergency/all', { params }),
  getById: (id) => api.get(`/emergency/${id}`),
  updateStatus: (id, status) => api.put(`/emergency/${id}/status`, { status }),
  cancel: (id) => api.put(`/emergency/${id}/cancel`),
};

// 2FA API
export const twoFAAPI = {
  getStatus: () => api.get('/2fa/status'),
  setup: (method) => api.post('/2fa/setup', { method }),
  verifyEnable: (token) => api.post('/2fa/verify-enable', { token }),
  disable: (token) => api.post('/2fa/disable', { token }),
  sendOTP: (method) => api.post('/2fa/send-otp', { method }),
  verifyOTP: (otp, sessionId) => api.post('/2fa/verify-otp', { otp, sessionId }),
};

// Session API
export const sessionAPI = {
  getSessions: () => api.get('/sessions'),
  getHistory: () => api.get('/sessions/history'),
  logoutOther: () => api.post('/sessions/logout-other'),
  logoutSession: (sessionId) => api.delete(`/sessions/${sessionId}`),
};

// Department API
export const departmentAPI = {
  getDepartments: (params) => api.get('/departments', { params }),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  getDoctors: (deptId) => api.get(`/departments/${deptId}/doctors`),
};

// Bed Management API
export const bedAPI = {
  getBeds: (params) => api.get('/beds', { params }),
  create: (data) => api.post('/beds', data),
  update: (id, data) => api.put(`/beds/${id}`, data),
  delete: (id) => api.delete(`/beds/${id}`),
  admit: (bedId, data) => api.post(`/beds/${bedId}/admit`, data),
  discharge: (bedId) => api.put(`/beds/${bedId}/discharge`),
  getStats: () => api.get('/beds/stats'),
};

// Earnings API
export const earningsAPI = {
  startCall: (data) => api.post('/earnings/start-call', data),
  endCall: (data) => api.post('/earnings/end-call', data),
  checkRejoinStatus: (consultationId) => api.get(`/earnings/rejoin-status/${consultationId}`),
  getDoctorEarnings: (params) => api.get('/earnings/doctor/history', { params }),
  getPendingEarnings: () => api.get('/earnings/doctor/pending')
};

// Rules API
export const ruleAPI = {
  create: (data) => api.post('/rules', data),
  getActive: () => api.get('/rules'),
  accept: (ruleId) => api.post('/rules/accept', { ruleId }),
  getPending: () => api.get('/rules/pending')
};

// Partner API
export const partnerAPI = {
  getPublic: (type) => api.get('/partners', { params: { type } }),
  getAll: () => api.get('/admin/partners'),
  create: (data) => api.post('/admin/partners', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/admin/partners/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/partners/${id}`)
};

export default api;